import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Nav, Tab, Badge, Button, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ProviderDashboard from './ProviderDashboard';
import StatisticsDashboard from './StatisticsDashboard';
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import './dashboard-styles.css';

const MainDashboard = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('providerStatus');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date()
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'compact'

  // Effet pour mettre à jour périodiquement l'heure de la dernière actualisation
  useEffect(() => {
    const timer = setInterval(() => {
      // Juste pour forcer le rendu et mettre à jour le temps écoulé depuis la dernière actualisation
      setLastRefresh(prev => prev);
    }, 60000); // Mise à jour toutes les minutes
    
    return () => clearInterval(timer);
  }, []);

  // Fonction pour obtenir la différence de temps depuis la dernière actualisation
  const getTimeAgo = () => {
    const now = new Date();
    const diffMs = now - lastRefresh;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "à l'instant";
    if (diffMins === 1) return "il y a 1 minute";
    if (diffMins < 60) return `il y a ${diffMins} minutes`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return "il y a 1 heure";
    return `il y a ${diffHours} heures`;
  };

  // Gestionnaire pour les dates
  const handleDateChange = (type, date) => {
    setDateRange(prev => ({
      ...prev,
      [type]: date
    }));
  };

  // Gestionnaire pour l'actualisation manuelle
  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1);
    setLastRefresh(new Date());
  };

  // Raccourcis pour les périodes prédéfinies
  const setQuickDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setDateRange({
      startDate: start,
      endDate: end
    });
    
    handleRefresh();
  };

  return (
    <div className="dashboard-wrapper">
      {/* En-tête du Dashboard avec titre et contrôles */}
      <div className="dashboard-header mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center">
          <div className="dashboard-title">
            <h2 className="text-primary font-weight-bold mb-1">Tableau de Bord A-CARE</h2>
            <p className="mb-0 text-muted">
              <i className="fa fa-info-circle mr-1"></i>
              Vue d'ensemble des prestataires et des statistiques d'utilisation
            </p>
          </div>
          
          <div className="dashboard-actions d-flex align-items-center flex-wrap">
            <div className="date-selector mr-3 mt-2">
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={fr}>
                <div className="d-flex">
                  <DatePicker
                    autoOk
                    variant="inline"
                    format="dd/MM/yyyy"
                    label="De"
                    value={dateRange.startDate}
                    onChange={(date) => handleDateChange('startDate', date)}
                    className="mr-2"
                    size="small"
                    inputVariant="outlined"
                  />
                  <DatePicker
                    autoOk
                    variant="inline"
                    format="dd/MM/yyyy"
                    label="À"
                    value={dateRange.endDate}
                    onChange={(date) => handleDateChange('endDate', date)}
                    size="small"
                    inputVariant="outlined"
                  />
                </div>
              </MuiPickersUtilsProvider>
            </div>
            
            <div className="quick-actions d-flex mt-2">
              <Dropdown className="mr-2">
                <Dropdown.Toggle variant="outline-primary" size="sm" id="period-dropdown">
                  <i className="fa fa-calendar-alt mr-1"></i> Période
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setQuickDateRange(1)}>Aujourd'hui</Dropdown.Item>
                  <Dropdown.Item onClick={() => setQuickDateRange(7)}>7 derniers jours</Dropdown.Item>
                  <Dropdown.Item onClick={() => setQuickDateRange(30)}>30 derniers jours</Dropdown.Item>
                  <Dropdown.Item onClick={() => setQuickDateRange(90)}>90 derniers jours</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleRefresh}
                className="d-flex align-items-center"
              >
                <i className="fa fa-sync-alt mr-1"></i> Actualiser
              </Button>
              
              <div className="view-toggle ml-2">
                <Button 
                  variant={viewMode === 'grid' ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="mr-1"
                >
                  <i className="fa fa-th-large"></i>
                </Button>
                <Button 
                  variant={viewMode === 'compact' ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setViewMode('compact')}
                >
                  <i className="fa fa-list"></i>
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="dashboard-info d-flex justify-content-between align-items-center mt-3">
          <div className="info-item">
            <Badge variant="light" className="p-2">
              <i className="fa fa-clock text-info mr-1"></i> Dernière actualisation: {getTimeAgo()}
            </Badge>
          </div>
          <div className="info-date">
            <Badge variant="light" className="p-2">
              <i className="fa fa-calendar text-info mr-1"></i> 
              Période: {format(dateRange.startDate, 'dd MMM yyyy')} - {format(dateRange.endDate, 'dd MMM yyyy')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Navigation par onglets avec icônes et compteurs */}
      <Tab.Container defaultActiveKey={activeTab} onSelect={(key) => setActiveTab(key)}>
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                <Nav className="nav-tabs-modern" variant="tabs">
                  <Nav.Item className="flex-fill">
                    <Nav.Link 
                      eventKey="providerStatus" 
                      className={`font-weight-medium py-3 px-4 text-center ${activeTab === 'providerStatus' ? 'active' : ''}`}
                    >
                      <div className="d-flex flex-column align-items-center">
                        <i className="fa fa-users mb-2 fa-lg"></i>
                        <span>Statut des prestataires</span>
                        <Badge variant={activeTab === 'providerStatus' ? "primary" : "light"} className="mt-2">
                          Suivi en temps réel
                        </Badge>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="flex-fill">
                    <Nav.Link 
                      eventKey="statistics" 
                      className={`font-weight-medium py-3 px-4 text-center ${activeTab === 'statistics' ? 'active' : ''}`}
                    >
                      <div className="d-flex flex-column align-items-center">
                        <i className="fa fa-chart-line mb-2 fa-lg"></i>
                        <span>Statistiques d'utilisation</span>
                        <Badge variant={activeTab === 'statistics' ? "primary" : "light"} className="mt-2">
                          Analyse de données
                        </Badge>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tab.Content>
          <Tab.Pane eventKey="providerStatus">
            <ProviderDashboard 
              dateRange={dateRange} 
              refreshTrigger={refreshCounter}
              viewMode={viewMode}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="statistics">
            <StatisticsDashboard 
              dateRange={dateRange} 
              refreshTrigger={refreshCounter}
              viewMode={viewMode}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default MainDashboard;